import { TouchableOpacity, View } from "react-native"
import React, { useState } from "react"
import Slider from "@react-native-community/slider"
import BFText from "@/components/standard/BFText"
import {
  type CustomCompDescriptionType,
  type CustomCompOptionProps,
} from "@/utils/CustomizeCompsOptions"
import CustomCompAdjustmentView from "@/components/customizecomps/CustomCompAdjustmentView"
import { BFColors } from "@/constants/Colors"
import { numberWithCommas } from "@/utils/NumberUtils"

interface CustomCompSliderRangeProps {
  title: string
  subjectValue: number | null
  minOptions: CustomCompOptionProps
  maxOptions: CustomCompOptionProps
  onValuesChanged: (minVal: number | null, maxVal: number | null) => void
  middleIcon: React.ReactNode
  label?: string
  sublabel?: string
  stepSize?: number
  initialMin?: number | null
  initialMax?: number | null
  descriptionType: CustomCompDescriptionType
}

const CustomCompSliderRange: React.FC<CustomCompSliderRangeProps> = ({
  title,
  subjectValue,
  minOptions,
  maxOptions,
  onValuesChanged,
  middleIcon,
  label,
  sublabel,
  stepSize,
  initialMin,
  initialMax,
  descriptionType = "Standard",
}: CustomCompSliderRangeProps) => {
  const [minValue, setMinValue] = useState<number | null>(
    initialMin ?? (minOptions.options[0].value as number)
  )
  const [maxValue, setMaxValue] = useState<number | null>(
    initialMax ??
      (maxOptions.options[maxOptions.options.length - 1].value as number)
  )

  const handleMinValueChange = (value: number) => {
    setMinValue(value)
    onValuesChanged(value, maxValue)
  }

  const handleMaxValueChange = (value: number) => {
    setMaxValue(value)
    onValuesChanged(minValue, value)
  }

  const minStepSize =
    stepSize ??
    (minOptions.options.length > 1
      ? (minOptions.options[1].value as number) -
        (minOptions.options[0].value as number)
      : 1)

  const maxStepSize =
    stepSize ??
    (maxOptions.options.length > 1
      ? (maxOptions.options[1].value as number) -
        (maxOptions.options[0].value as number)
      : 1)

  const effectiveMaxReadout =
    maxValue === maxOptions.options[0].value ? subjectValue : maxValue

  let minDescription: string = ""
  let maxDescription: string = ""
  if (descriptionType === "Standard") {
    minDescription = "Less"
    maxDescription = "More"
  } else if (descriptionType === "Percentage" && subjectValue !== null) {
    const minPercentage = ((minValue! - subjectValue) / subjectValue) * 100
    const maxPercentage = ((maxValue! - subjectValue) / subjectValue) * 100
    minDescription = `${Math.round(Math.abs(minPercentage))}% Less`
    maxDescription = `${Math.round(maxPercentage)}% More`
  } else if (descriptionType === "Months and Years ago") {
    minDescription = "Months ago"
    minDescription = "Years ago"
  }

  const renderOptions = (
    options: Array<{ name: string | null; value: string | number | null }>,
    isMin: boolean
  ) => {
    return options.map((option, index) => (
      <TouchableOpacity
        testID={`option-${option?.value}`}
        key={index}
        onPress={() => {
          if (isMin) {
            handleMinValueChange(option.value as number)
          } else {
            handleMaxValueChange(option.value as number)
          }
        }}
        className="flex-1 items-center"
        hitSlop={8}
      >
        <BFText className="text-calm-blue text-[12px] font-spacegrotesk">
          {option.name}
        </BFText>
      </TouchableOpacity>
    ))
  }

  return (
    <CustomCompAdjustmentView title={title} label={label} sublabel={sublabel}>
      <View className="flex flex-row items-center justify-between px-5 w-full">
        <View className="flex-1">
          <BFText className="text-[16px] text-calm-blue font-figtree-extrabold mr-2 pb-[8px]">
            {numberWithCommas(minValue || 0)}
          </BFText>
          <Slider
            style={{ flex: 1, marginRight: 10 }}
            thumbImage={require("@/assets/slider-thumb.png")}
            value={minValue || 0}
            minimumValue={minOptions.options[0].value as number}
            maximumValue={
              minOptions.options[minOptions.options.length - 1].value as number
            }
            step={minStepSize}
            minimumTrackTintColor={BFColors.lightGrey}
            maximumTrackTintColor={BFColors.calmBlue}
            onValueChange={handleMinValueChange}
          />
        </View>
        <View className="pt-8 w-2 items-center">{middleIcon}</View>
        <View className="flex-1 self-end">
          <BFText className="text-[16px] text-calm-blue font-figtree-extrabold mr-2 pb-[8px] text-right">
            {numberWithCommas(effectiveMaxReadout || 0)}
          </BFText>

          <Slider
            style={{ flex: 1, marginLeft: 10 }}
            thumbImage={require("@/assets/slider-thumb.png")}
            value={maxValue || 0}
            minimumValue={maxOptions.options[0].value as number}
            maximumValue={
              maxOptions.options[maxOptions.options.length - 1].value as number
            }
            step={maxStepSize}
            minimumTrackTintColor={BFColors.calmBlue}
            maximumTrackTintColor={BFColors.lightGrey}
            onValueChange={handleMaxValueChange}
          />
        </View>
      </View>
      <View className="flex flex-row justify-between mt-2 w-full px-6">
        {renderOptions(minOptions.options, true)}
        {renderOptions(maxOptions.options, false)}
      </View>

      <View className="flex-row items-end px-5 pb-3">
        <BFText className="flex-1 self-start mt-3 font-spacegrotesk leading-none tracking-tight text-gray-800 text-[11px]">
          {minDescription}
        </BFText>
        <BFText className="flex-1 self-end text-right mt-3 font-spacegrotesk leading-none tracking-tight text-gray-800 text-[11px]">
          {maxDescription}
        </BFText>
      </View>
    </CustomCompAdjustmentView>
  )
}

export default CustomCompSliderRange
